import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { takeWebOauthProvider } from '../screens/auth/AuthPieces.js';
import { subscribeEnglishState } from '../screens/leveling/useEnglishLevel.js';
import {
  analyticsIdentify,
  analyticsReset,
  analyticsSetOptOut,
  analyticsSetPersonProperties,
  track,
} from './analytics.js';
import { isNative } from './platform.js';

/**
 * Ties PostHog (lib/analytics.ts) to the session. Mounted once INSIDE `<AuthProvider>`;
 * renders nothing and is a no-op while analytics is off.
 *
 * - Signed in → the account's «Estadísticas de uso» switch (`user.analyticsOptOut`)
 *   first, then `identify(<internal UUID>)` (never the email or the name) — an
 *   opted-out account is never identified and sends nothing. Re-applied whenever the
 *   switch changes (Perfil refreshes the session after saving it).
 * - Signed out → `reset()`. Sign-out and account deletion (DeleteAccountSection signs
 *   out after the delete) both land here, as does an expired session; an account
 *   switch resets before identifying the new one.
 * - Web Google / Apple sign-in ends in a full-page redirect, so its
 *   `sign_in_completed` fires here: the provider the redirect started with is still in
 *   sessionStorage (AuthPieces takes it only on an error return). OTP and native
 *   sign-ins fire theirs at the call site.
 * - The CEFR level as a person property, from every fresh `/state` read.
 */
export function AnalyticsBridge(): null {
  const { user } = useAuth();
  const id = user?.id ?? null;
  const optOut = user?.analyticsOptOut === true;
  const prevRef = useRef<string | null>(null);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = id;
    if (prev && id !== prev) analyticsReset();
    if (!id) return;
    // Before identify: an opted-out account must not send even its $identify.
    analyticsSetOptOut(optOut);
    analyticsIdentify(id);
    if (id === prev) return; // only the switch changed
    if (!isNative) {
      const provider = takeWebOauthProvider();
      if (provider) {
        const path = window.location.pathname;
        track('sign_in_completed', {
          method: provider,
          new_user: path === '/welcome' ? true : path === '/home' ? false : null,
        });
      }
    }
  }, [id, optOut]);

  useEffect(
    () =>
      subscribeEnglishState((state) => {
        if (state.cefrGlobal) analyticsSetPersonProperties({ cefr_level: state.cefrGlobal });
      }),
    [],
  );

  return null;
}
